import {StorageHelper} from "../../helpers/storage.helper";
import {parseStored, type TimerState} from "./timer.state";
import {subscribeKeys} from "./storage-subscription";

/**
 * Where a timer tile keeps what belongs to ONE viewer (feature plan 0073): its run and whether its sound is off.
 * Browser storage, per tile — like the stopwatch (`stopwatch.store.ts`), so each viewer's timer runs for itself
 * and survives a reload.
 *
 * Two kinds of callers: the element knows its tile id; the app-wide watcher (`services/timer-watch.ts`) knows no
 * tiles, only the stored keys. So everything works on the **finished key** — the form `StorageHelper.buildKey`
 * writes (`ha.` prefix, lower case, separators folded). The tile id cannot be recovered from it, and nothing
 * tries to. The per-tile functions are thin wrappers over the key functions: one way to read, one to write.
 * They go to `localStorage` directly (through `StorageHelper` the finished key would get a second `ha.`).
 *
 * Every access is guarded with a `console.warn`: a blocked or full storage makes the timer forget, never fail.
 */
const STATE_PREFIX = StorageHelper.buildKey('gimmicks.timer.');
const MUTE_PREFIX = StorageHelper.buildKey('gimmicks.timer-mute.');

/** The finished key of a tile's run. */
export function timerKeyOf(tileId: string): string {
    return StorageHelper.buildKey(`gimmicks.timer.${tileId}`);
}

/** The finished key of a tile's sound switch, from its run's key. */
export function muteKeyOf(timerKey: string): string {
    return MUTE_PREFIX + timerKey.slice(STATE_PREFIX.length);
}

export function isTimerKey(key: string | null): key is string {
    return key !== null && key.startsWith(STATE_PREFIX);
}

/** All stored timer runs (sound and look switches have other prefixes: `timer-mute.`, `timer-skin.`). */
export function listTimerKeys(): string[] {
    const keys: string[] = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (isTimerKey(key)) {
                keys.push(key);
            }
        }
    } catch (error) {
        console.warn('The timers could not be listed', error);
    }
    return keys;
}

/** A stored run, or `null` when there is none or it is not valid (see `parseStored`). */
export function readStateAt(key: string, now: number): TimerState | null {
    try {
        const raw = localStorage.getItem(key);
        return raw === null ? null : parseStored(JSON.parse(raw), now);
    } catch (error) {
        console.warn('The timer state could not be read', error);
        return null;
    }
}

/** Whether writing worked. */
export function writeStateAt(key: string, state: TimerState): boolean {
    try {
        localStorage.setItem(key, JSON.stringify(state));
        return true;
    } catch (error) {
        console.warn('The timer state could not be stored', error);
        return false;
    }
}

export function readMutedAt(key: string): boolean {
    try {
        return localStorage.getItem(muteKeyOf(key)) === 'true';
    } catch (error) {
        console.warn('The timer sound switch could not be read', error);
        return false;
    }
}

export function writeMutedAt(key: string, muted: boolean): void {
    try {
        localStorage.setItem(muteKeyOf(key), muted ? 'true' : 'false');
    } catch (error) {
        console.warn('The timer sound switch could not be stored', error);
    }
}

export function loadState(tileId: string, now: number): TimerState | null {
    return readStateAt(timerKeyOf(tileId), now);
}

export function saveState(tileId: string, state: TimerState): boolean {
    return writeStateAt(timerKeyOf(tileId), state);
}

export function loadMuted(tileId: string): boolean {
    return readMutedAt(timerKeyOf(tileId));
}

export function saveMuted(tileId: string, muted: boolean): void {
    writeMutedAt(timerKeyOf(tileId), muted);
}

/** `onChange` when another tab changed this tile's run or sound switch. */
export function subscribe(tileId: string, onChange: () => void): () => void {
    return subscribeKeys([`gimmicks.timer.${tileId}`, `gimmicks.timer-mute.${tileId}`], onChange);
}
